/**
 * Utilities for Custom Section data normalization, serialization, and layout handling.
 * Shared between ContentPreview (canvas editor & preview) and htmlGenerator (iframe & static ZIP export).
 */

export type CustomSectionLayout = 'story' | 'cardGrid' | 'alternating';
export type CustomBlockMediaType = 'icon' | 'image' | 'none';

export interface CustomSectionBlock {
  id: string;
  mediaType?: CustomBlockMediaType;
  image?: string;
  title?: string;
  body: string;
  icon?: string;
  iconType?: 'preset' | 'custom';
  customIcon?: string;
}

export interface CustomSectionPayload {
  layout: CustomSectionLayout;
  blocks: CustomSectionBlock[];
}

export const ALLOWED_CUSTOM_LAYOUTS: CustomSectionLayout[] = [
  'story',
  'cardGrid',
  'alternating',
];

export const MAX_CUSTOM_BLOCKS = 6;

/**
 * Creates a fresh default block.
 */
export function createDefaultBlock(index = 1): CustomSectionBlock {
  return {
    id: `block_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    mediaType: 'icon',
    title: '',
    body: '',
    image: '',
    icon: 'ri-star-line',
    iconType: 'preset',
    customIcon: '',
  };
}

/**
 * Sanitizes rich text HTML by removing dangerous tags or attributes.
 */
export function sanitizeRichText(html: string): string {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/javascript:[^"']*/gi, '');
}

/**
 * Normalizes any format of custom section content into a standardized CustomSectionPayload.
 * Handles:
 * - New structured JSON string: { layout: 'cardGrid', blocks: [...] }
 * - Legacy JSON string: { text: "...", image: "..." } or { content: "...", image: "..." }
 * - Legacy plain string: "..." or HTML string
 * - Raw object or array of objects
 * - Corrupted JSON / empty state
 */
export function normalizeCustomSection(rawContent: any): CustomSectionPayload {
  let parsed: any = null;

  if (typeof rawContent === 'string') {
    const trimmed = rawContent.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        // Plain string fallback
        parsed = { text: rawContent };
      }
    } else {
      parsed = { text: rawContent };
    }
  } else if (typeof rawContent === 'object' && rawContent !== null) {
    parsed = rawContent;
  }

  // If parsed is an array
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) {
      return {
        layout: 'story',
        blocks: [createDefaultBlock(1)],
      };
    }
    const blocks: CustomSectionBlock[] = parsed.slice(0, MAX_CUSTOM_BLOCKS).map((item, idx) => {
      if (typeof item === 'string') {
        try {
          const inner = JSON.parse(item);
          const hasImg = !!inner.image;
          return {
            id: inner.id || `b_${idx + 1}`,
            mediaType: inner.mediaType || (hasImg ? 'image' : 'icon'),
            title: inner.title || '',
            body: inner.body || inner.text || inner.content || inner.description || item,
            image: inner.image || '',
            icon: inner.icon || 'ri-star-line',
            iconType: inner.iconType === 'custom' ? 'custom' : 'preset',
            customIcon: inner.customIcon || '',
          };
        } catch {
          return {
            id: `b_${idx + 1}`,
            mediaType: 'icon',
            title: '',
            body: item,
            image: '',
            icon: 'ri-star-line',
            iconType: 'preset',
            customIcon: '',
          };
        }
      }
      const hasImg = !!item.image;
      return {
        id: item.id || `b_${idx + 1}`,
        mediaType: item.mediaType || (hasImg ? 'image' : 'icon'),
        title: item.title || '',
        body: item.body || item.text || item.content || item.description || '',
        image: item.image || '',
        icon: item.icon || 'ri-star-line',
        iconType: item.iconType === 'custom' ? 'custom' : 'preset',
        customIcon: item.customIcon || '',
      };
    });

    return {
      layout: blocks.length > 1 ? 'cardGrid' : 'story',
      blocks,
    };
  }

  if (parsed && typeof parsed === 'object') {
    // Check if it already has layout + blocks
    if (Array.isArray(parsed.blocks)) {
      let validLayout: CustomSectionLayout = 'story';
      if (parsed.layout === 'cardGrid' || parsed.layout === 'gallery') {
        validLayout = 'cardGrid';
      } else if (parsed.layout === 'alternating') {
        validLayout = 'alternating';
      }

      const blocks: CustomSectionBlock[] = parsed.blocks
        .slice(0, MAX_CUSTOM_BLOCKS)
        .map((b: any, idx: number) => {
          const hasImg = !!b.image;
          const mediaType: CustomBlockMediaType = b.mediaType || (hasImg ? 'image' : 'icon');
          return {
            id: b.id || `b_${idx + 1}`,
            mediaType,
            title: b.title || '',
            body: b.body !== undefined ? b.body : (b.text || b.content || b.description || ''),
            image: b.image || '',
            icon: b.icon || 'ri-star-line',
            iconType: b.iconType === 'custom' ? 'custom' : 'preset',
            customIcon: b.customIcon || '',
          };
        });

      return {
        layout: validLayout,
        blocks: blocks.length > 0 ? blocks : [createDefaultBlock(1)],
      };
    }

    // Legacy shape: { text / content / description, image }
    const text = parsed.text || parsed.content || parsed.description || parsed.body || '';
    const image = parsed.image || '';
    const title = parsed.title || '';

    return {
      layout: 'story',
      blocks: [
        {
          id: 'b_1',
          mediaType: image ? 'image' : 'icon',
          title,
          body: text,
          image,
          icon: 'ri-star-line',
          iconType: 'preset',
          customIcon: '',
        },
      ],
    };
  }

  // Default empty state
  return {
    layout: 'story',
    blocks: [createDefaultBlock(1)],
  };
}

/**
 * Serializes layout and blocks into a standardized JSON string.
 */
export function serializeCustomSection(
  layout: CustomSectionLayout,
  blocks: CustomSectionBlock[]
): string {
  const validLayout = ALLOWED_CUSTOM_LAYOUTS.includes(layout) ? layout : 'story';
  const cleanBlocks = blocks.slice(0, MAX_CUSTOM_BLOCKS).map((b, idx) => ({
    id: b.id || `b_${idx + 1}`,
    mediaType: b.mediaType || (b.image ? 'image' : 'icon'),
    title: (b.title || '').trim(),
    body: sanitizeRichText(b.body || ''),
    image: (b.image || '').trim(),
    icon: (b.icon || 'ri-star-line').trim(),
    iconType: b.iconType === 'custom' ? 'custom' : 'preset',
    customIcon: (b.customIcon || '').trim(),
  }));

  return JSON.stringify({
    layout: validLayout,
    blocks: cleanBlocks,
  });
}
